// src/pages/Pricing.jsx
// R11: Pricing/Subscription Page for FlightSearch (Airgati)

import React, { useState } from 'react';
import './Pricing.css';

// Simple navigation function (no react-router-dom needed)
const navigateTo = (path) => {
  window.location.href = path;
};

const Pricing = ({ user, onSubscribe }) => {
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showCanceledMessage, setShowCanceledMessage] = useState(false);

  // Check URL params for success/canceled status
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setShowSuccessMessage(true);
      // Clean up URL
      window.history.replaceState({}, '', '/pricing');
    }
    if (params.get('canceled') === 'true') {
      setShowCanceledMessage(true);
      // Clean up URL
      window.history.replaceState({}, '', '/pricing');
    }
  }, []);

  // Pricing data
  const plans = [
    {
      id: 'free',
      name: 'Free',
      description: 'Perfect for casual travelers',
      monthlyPrice: 0,
      annualPrice: 0,
      features: [
        { name: 'Flight searches', value: '100/month', included: true },
        { name: 'AI recommendations', value: 'Basic', included: true },
        { name: 'Price predictions', value: true, included: true },
        { name: 'Jet lag optimizer', value: true, included: true },
        { name: 'Price alerts (email)', value: false, included: false },
        { name: 'Priority support', value: false, included: false },
        { name: 'Ad-free experience', value: false, included: false },
      ],
      cta: 'Current Plan',
      popular: false,
      color: '#6b7280', // gray
    },
    {
      id: 'basic',
      name: 'Basic',
      description: 'For regular travelers',
      monthlyPrice: 4.99,
      annualPrice: 49.99, // ~2 months free
      features: [
        { name: 'Flight searches', value: '250/month', included: true },
        { name: 'AI recommendations', value: 'Basic', included: true },
        { name: 'Price predictions', value: true, included: true },
        { name: 'Jet lag optimizer', value: true, included: true },
        { name: 'Price alerts (email)', value: '10/month', included: true },
        { name: 'Priority support', value: false, included: false },
        { name: 'Ad-free experience', value: false, included: false },
      ],
      cta: 'Get Started',
      popular: false,
      color: '#3b82f6', // blue
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'For frequent flyers',
      monthlyPrice: 9.99,
      annualPrice: 99.99, // ~2 months free
      features: [
        { name: 'Flight searches', value: 'Unlimited', included: true },
        { name: 'AI recommendations', value: 'Advanced', included: true },
        { name: 'Price predictions', value: true, included: true },
        { name: 'Jet lag optimizer', value: true, included: true },
        { name: 'Price alerts (email)', value: '25 alerts', included: true },
        { name: 'Priority support', value: true, included: true },
        { name: 'Ad-free experience', value: false, included: false },
      ],
      cta: 'Get Pro',
      popular: true,
      color: '#8b5cf6', // purple
    },
    {
      id: 'max',
      name: 'Max',
      description: 'For power users & teams',
      monthlyPrice: 19.99,
      annualPrice: 199.99, // ~2 months free
      features: [
        { name: 'Flight searches', value: 'Unlimited', included: true },
        { name: 'AI recommendations', value: 'Premium', included: true },
        { name: 'Price predictions', value: true, included: true },
        { name: 'Jet lag optimizer', value: true, included: true },
        { name: 'Price alerts (email)', value: '100 alerts', included: true },
        { name: 'Priority support', value: true, included: true },
        { name: 'Ad-free experience', value: true, included: true },
      ],
      cta: 'Get Max',
      popular: false,
      color: '#f59e0b', // amber/gold
    },
  ];

  const getPrice = (plan) => {
    if (billingCycle === 'annual') {
      return plan.annualPrice;
    }
    return plan.monthlyPrice;
  };

  const getMonthlyEquivalent = (plan) => {
    if (billingCycle === 'annual' && plan.annualPrice > 0) {
      return (plan.annualPrice / 12).toFixed(2);
    }
    return plan.monthlyPrice;
  };

  const getSavings = (plan) => {
    if (plan.monthlyPrice === 0) return 0;
    const annualIfMonthly = plan.monthlyPrice * 12;
    const savings = annualIfMonthly - plan.annualPrice;
    return savings.toFixed(0);
  };

  const handleSelectPlan = (plan) => {
    if (plan.id === 'free') {
      navigateTo('/');
      return;
    }
    
    setSelectedPlan(plan.id);
    
    // If user is not logged in, redirect to sign in
    if (!user) {
      // Store selected plan in localStorage to retrieve after login
      localStorage.setItem('selectedPlan', plan.id);
      localStorage.setItem('billingCycle', billingCycle);
      // Trigger sign in modal or redirect
      if (onSubscribe) {
        onSubscribe(plan.id, billingCycle);
      } else {
        // Redirect to home with sign-in prompt
        navigateTo('/?signin=true&plan=' + plan.id + '&billing=' + billingCycle);
      }
      return;
    }
    
    // If user is logged in, proceed to checkout
    handleCheckout(plan);
  };

  const handleCheckout = async (plan) => {
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: plan.id,
          billingCycle: billingCycle,
          userId: user.id,
          userEmail: user.email,
          successUrl: window.location.origin + '/pricing?success=true',
          cancelUrl: window.location.origin + '/pricing?canceled=true',
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }

    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pricing-page">
      {/* Header - Matches Main App */}
      <div className="header">
        <div className="header-content">
          <a href="/" className="logo">
            <img src="/logo.png" alt="Airgati" className="logo-image" />
          </a>
          
          <div className="nav">
         {/*    <a href="/#how-it-works">How it Works</a> */}
            <a href="/#features">Features</a>
            <a href="/pricing" className="active">Pricing</a>
          </div>
          
          <div className="header-actions">
            {user ? (
              <a 
                href="/account" 
                className="user-account-btn"
              >
                <span className="user-icon">👤</span>
                My Account
              </a>
            ) : (
              <button 
                className="sign-in-btn" 
                onClick={() => navigateTo('/?signin=true')}
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="status-banner success">
          <span className="status-icon">🎉</span>
          <div className="status-content">
            <strong>Welcome to your new plan!</strong>
            <p>Your subscription is now active. Enjoy your upgraded features!</p>
          </div>
          <button className="status-close" onClick={() => setShowSuccessMessage(false)}>✕</button>
        </div>
      )}

      {/* Canceled Message */}
      {showCanceledMessage && (
        <div className="status-banner canceled">
          <span className="status-icon">ℹ️</span>
          <div className="status-content">
            <strong>Checkout canceled</strong>
            <p>No worries! You can subscribe anytime when you're ready.</p>
          </div>
          <button className="status-close" onClick={() => setShowCanceledMessage(false)}>✕</button>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Redirecting to checkout...</p>
        </div>
      )}

      {/* Hero Section */}
      <section className="pricing-hero">
        <h1>Simple, Transparent Pricing</h1>
        <p>Choose the perfect plan for your travel needs. No hidden fees.</p>
      </section>

      {/* Billing Toggle - Separate Section */}
      <div className="toggle-container">
        <div className="billing-toggle">
          <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
          <button 
            className={`toggle-switch ${billingCycle === 'annual' ? 'annual' : ''}`}
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
            aria-label="Toggle billing cycle"
          >
            <span className="toggle-knob"></span>
          </button>
          <span className={billingCycle === 'annual' ? 'active' : ''}>
            Annual
            <span className="save-badge">Save 17%</span>
          </span>
        </div>
      </div>

      {/* Pricing Cards */}
      <section className="pricing-cards">
        {plans.map((plan) => (
          <div 
            key={plan.id} 
            className={`pricing-card ${plan.popular ? 'popular' : ''} ${selectedPlan === plan.id ? 'selected' : ''}`}
          >
            {plan.popular && <div className="popular-badge">Most Popular</div>}
            
            <div className="card-header" style={{ borderColor: plan.color }}>
              <h2 className="plan-name">{plan.name}</h2>
              <p className="plan-description">{plan.description}</p>
            </div>
            
            <div className="card-pricing">
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">
                  {billingCycle === 'annual' && plan.monthlyPrice > 0 
                    ? getMonthlyEquivalent(plan) 
                    : plan.monthlyPrice}
                </span>
                <span className="period">/month</span>
              </div>
              
              {billingCycle === 'annual' && plan.monthlyPrice > 0 && (
                <div className="annual-total">
                  Billed ${plan.annualPrice}/year
                  <span className="savings">Save ${getSavings(plan)}</span>
                </div>
              )}
              
              {plan.monthlyPrice === 0 && (
                <div className="free-label">Free forever</div>
              )}
            </div>
            
            <ul className="features-list">
              {plan.features.map((feature, index) => (
                <li key={index} className={feature.included ? 'included' : 'not-included'}>
                  <span className="feature-icon">
                    {feature.included ? '✓' : '✕'}
                  </span>
                  <span className="feature-name">{feature.name}</span>
                  {typeof feature.value === 'string' && (
                    <span className="feature-value">{feature.value}</span>
                  )}
                </li>
              ))}
            </ul>
            
            <button 
              className={`cta-button ${plan.id === 'free' ? 'free' : ''}`}
              style={{ backgroundColor: plan.id !== 'free' ? plan.color : undefined }}
              onClick={() => handleSelectPlan(plan)}
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </section>

      {/* Feature Comparison Table */}
      <section className="comparison-section" id="features">
        <h2>Compare All Features</h2>
        <div className="comparison-table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th>Basic</th>
                <th className="highlight">Pro</th>
                <th>Max</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Flight searches</td>
                <td>100/month</td>
                <td>250/month</td>
                <td className="highlight">Unlimited</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td>AI recommendations</td>
                <td>Basic</td>
                <td>Basic</td>
                <td className="highlight">Advanced</td>
                <td>Premium</td>
              </tr>
              <tr>
                <td>Price predictions</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Jet lag optimizer</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="highlight"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Price alerts (email)</td>
                <td><span className="cross">✕</span></td>
                <td>10/month</td>
                <td className="highlight">25 alerts</td>
                <td>100 alerts</td>
              </tr>
              <tr>
                <td>Priority support</td>
                <td><span className="cross">✕</span></td>
                <td><span className="cross">✕</span></td>
                <td className="highlight"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td>Ad-free experience</td>
                <td><span className="cross">✕</span></td>
                <td><span className="cross">✕</span></td>
                <td className="highlight"><span className="cross">✕</span></td>
                <td><span className="check">✓</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>Can I change my plan later?</h3>
            <p>Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any differences.</p>
          </div>
          <div className="faq-item">
            <h3>What payment methods do you accept?</h3>
            <p>We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and Apple Pay through our secure payment processor.</p>
          </div>
          <div className="faq-item">
            <h3>Is there a free trial?</h3>
            <p>Our Free plan is always available with no time limit. You can use it indefinitely and upgrade whenever you need more features.</p>
          </div>
          <div className="faq-item">
            <h3>What happens if I exceed my search limit?</h3>
            <p>You'll receive a notification when you're close to your limit. You can upgrade your plan or wait until the next month when your quota resets.</p>
          </div>
          <div className="faq-item">
            <h3>Can I cancel anytime?</h3>
            <p>Absolutely! There are no long-term contracts. Cancel anytime from your account settings, and you'll retain access until the end of your billing period.</p>
          </div>
          <div className="faq-item">
            <h3>Do you offer refunds?</h3>
            <p>Yes, we offer a 7-day money-back guarantee. If you're not satisfied within the first week, contact us for a full refund.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bottom-cta">
        <h2>Ready to travel smarter?</h2>
        <p>Join thousands of travelers who save time and money with Airgati.</p>
        <button 
          className="start-button"
          onClick={() => navigateTo('/')}
        >
          Start Searching Flights →
        </button>
      </section>

      {/* Footer */}
      <footer className="pricing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/logo.png" alt="Airgati" />
            <p>AI-powered flight search for smarter travel.</p>
          </div>
          <div className="footer-links">
            <div className="link-group">
              <h4>Product</h4>
              <a href="/">Search Flights</a>
              <a href="/pricing">Pricing</a>
              <a href="#features">Features</a>
            </div>
            <div className="link-group">
              <h4>Company</h4>
              <a href="/about">About Us</a>
              <a href="/contact">Contact</a>
              <a href="/careers">Careers</a>
            </div>
            <div className="link-group">
              <h4>Legal</h4>
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
              <a href="/cookies">Cookie Policy</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Airgati. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
